import React, { useCallback, useEffect, useState } from 'react';
import FloorPlan from './components/FloorPlan';
import DevMode from './components/DevMode';
import './App.css';

const SERVICE_OPTIONS = [
  { id: 'midi', label: 'Service midi' },
  { id: 'soir', label: 'Service soir' }
];

const DEFAULT_TABLES = [
  { id: 1, number: 1, capacity: 4, type: 'round', x: 80, y: 50, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 2, number: 2, capacity: 4, type: 'round', x: 1000, y: 80, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 3, number: 3, capacity: 4, type: 'long', x: 150, y: 150, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 4, number: 4, capacity: 4, type: 'rect', x: 350, y: 80, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 5, number: 5, capacity: 4, type: 'rect', x: 450, y: 80, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 6, number: 6, capacity: 4, type: 'rect', x: 550, y: 80, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 7, number: 7, capacity: 2, type: 'rect', x: 650, y: 80, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 8, number: 8, capacity: 2, type: 'rect-small', x: 350, y: 200, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 9, number: 9, capacity: 2, type: 'rect-small', x: 350, y: 280, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 10, number: 10, capacity: 2, type: 'rect-small', x: 350, y: 360, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 11, number: 11, capacity: 2, type: 'rect-small', x: 500, y: 200, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 12, number: 12, capacity: 2, type: 'rect-small', x: 500, y: 280, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 13, number: 13, capacity: 2, type: 'rect-small', x: 600, y: 200, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 14, number: 14, capacity: 4, type: 'rect', x: 600, y: 280, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 15, number: 15, capacity: 2, type: 'rect-small', x: 700, y: 200, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 16, number: 16, capacity: 2, type: 'rect-small', x: 700, y: 280, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 17, number: 17, capacity: 2, type: 'rect-small', x: 850, y: 200, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 18, number: 18, capacity: 2, type: 'rect-small', x: 850, y: 280, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 19, number: 19, capacity: 2, type: 'rect-small', x: 350, y: 450, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 20, number: 20, capacity: 2, type: 'rect-small', x: 500, y: 450, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 21, number: 21, capacity: 2, type: 'rect-small', x: 600, y: 450, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 22, number: 22, capacity: 2, type: 'rect-small', x: 700, y: 450, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null },
  { id: 23, number: 23, capacity: 2, type: 'rect-small', x: 850, y: 450, guests: 0, status: 'free', reservationName: '', reservationTime: '', groupId: null }
];

const normalizeTables = (tables) => tables.map((table) => {
  const guests = Number.isFinite(table.guests) ? Math.max(0, Math.min(table.guests, table.capacity)) : 0;
  return {
    ...table,
    guests,
    status: guests > 0 ? 'occupied' : (table.status || 'free'),
    reservationName: table.reservationName || '',
    reservationTime: table.reservationTime || '',
    groupId: table.groupId || null
  };
});

const parsePresetsFromStorage = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter((plan) => (
      plan
      && typeof plan.id === 'string'
      && typeof plan.name === 'string'
      && Array.isArray(plan.tables)
    ));
  } catch (err) {
    console.error('Erreur lors de la lecture des plans sauvegardes:', err);
    return [];
  }
};

function App() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDevOpen, setIsDevOpen] = useState(false);
  const [hasSavedFloorPlan, setHasSavedFloorPlan] = useState(false);
  const [savedFloorPlans, setSavedFloorPlans] = useState([]);
  const [selectedService, setSelectedService] = useState(() => localStorage.getItem('restaurant-last-viewed-service') || 'midi');
  const [isSwitchingService, setIsSwitchingService] = useState(false);

  const FLOOR_PLAN_TEMPLATE_KEY = 'restaurant-floor-plan-template';
  const getFloorPlanStateKey = (serviceId) => `restaurant-floor-plan-state-${serviceId}`;
  const getFloorPlanSnapshotKey = (serviceId) => `restaurant-floor-plan-snapshot-${serviceId}`;
  const getFloorPlanOriginKey = (serviceId) => `restaurant-floor-plan-origin-${serviceId}`;
  const getFloorPlanPresetsKey = (serviceId) => `restaurant-floor-plan-presets-${serviceId}`;

  const fetchTables = useCallback(async () => {
    try {
      setLoading(true);
      const defaultTables = normalizeTables(DEFAULT_TABLES);
      const savedState = localStorage.getItem(getFloorPlanStateKey(selectedService));
      let template = localStorage.getItem(FLOOR_PLAN_TEMPLATE_KEY);
      if (!template) {
        template = JSON.stringify(defaultTables);
        localStorage.setItem(FLOOR_PLAN_TEMPLATE_KEY, template);
      }

      const serviceOriginKey = getFloorPlanOriginKey(selectedService);
      let serviceOrigin = localStorage.getItem(serviceOriginKey);
      if (!serviceOrigin) {
        serviceOrigin = savedState || template;
        localStorage.setItem(serviceOriginKey, serviceOrigin);
      }

      if (savedState) {
        try {
          setTables(normalizeTables(JSON.parse(savedState)));
        } catch (parseError) {
          console.error('Impossible de relire le plan sauvegardé:', parseError);
          setTables(defaultTables);
        }
      } else {
        const parsedOrigin = normalizeTables(JSON.parse(serviceOrigin));
        localStorage.setItem(getFloorPlanStateKey(selectedService), JSON.stringify(parsedOrigin));
        setTables(parsedOrigin);
      }
      setError(null);
      setLoading(false);
      setIsSwitchingService(false);
    } catch (err) {
      console.error('Erreur lors de la récupération des tables:', err);
      setError('Impossible de charger le plan de salle');
      setLoading(false);
      setIsSwitchingService(false);
    }
  }, [selectedService]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  useEffect(() => {
    if (!loading && !isSwitchingService && tables.length > 0) {
      localStorage.setItem(getFloorPlanStateKey(selectedService), JSON.stringify(tables));
    }
  }, [tables, loading, selectedService, isSwitchingService]);

  const handleServiceChange = (nextService) => {
    if (nextService === selectedService) {
      return;
    }
    setIsSwitchingService(true);
    setSelectedService(nextService);
  };

  useEffect(() => {
    const savedSnapshot = localStorage.getItem(getFloorPlanSnapshotKey(selectedService));
    const presets = parsePresetsFromStorage(getFloorPlanPresetsKey(selectedService));
    setSavedFloorPlans(presets);
    setHasSavedFloorPlan(Boolean(savedSnapshot) || presets.length > 0);
    localStorage.setItem('restaurant-last-viewed-service', selectedService);
  }, [selectedService]);

  const handleSaveNamedFloorPlan = async (name, includeReservations) => {
    try {
      const trimmedName = (name || '').trim();
      if (!trimmedName) {
        return { ok: false, message: 'Donnez un nom au plan.' };
      }

      const sanitizedTables = tables.map((table) => {
        if (includeReservations) {
          return { ...table };
        }
        return {
          ...table,
          guests: 0,
          status: 'free',
          reservationName: '',
          reservationTime: ''
        };
      });

      const currentPresets = parsePresetsFromStorage(getFloorPlanPresetsKey(selectedService));
      const newPreset = {
        id: `plan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: trimmedName,
        includeReservations: Boolean(includeReservations),
        createdAt: new Date().toISOString(),
        tables: sanitizedTables
      };

      const nextPresets = [newPreset, ...currentPresets].slice(0, 40);
      localStorage.setItem(getFloorPlanPresetsKey(selectedService), JSON.stringify(nextPresets));
      setSavedFloorPlans(nextPresets);
      setHasSavedFloorPlan(true);
      return { ok: true, preset: newPreset };
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du plan nomme:', err);
      return { ok: false, message: 'Impossible de sauvegarder ce plan.' };
    }
  };

  const handleLoadNamedFloorPlan = async (planId) => {
    try {
      const presets = parsePresetsFromStorage(getFloorPlanPresetsKey(selectedService));
      const selectedPlan = presets.find((plan) => plan.id === planId);
      if (!selectedPlan) {
        return false;
      }

      setTables(selectedPlan.tables);
      localStorage.setItem(getFloorPlanStateKey(selectedService), JSON.stringify(selectedPlan.tables));

      return true;
    } catch (err) {
      console.error('Erreur lors du chargement d\'un plan nomme:', err);
      return false;
    }
  };

  const handleDeleteNamedFloorPlan = async (planId) => {
    try {
      const currentPresets = parsePresetsFromStorage(getFloorPlanPresetsKey(selectedService));
      const nextPresets = currentPresets.filter((plan) => plan.id !== planId);
      localStorage.setItem(getFloorPlanPresetsKey(selectedService), JSON.stringify(nextPresets));
      setSavedFloorPlans(nextPresets);

      const savedSnapshot = localStorage.getItem(getFloorPlanSnapshotKey(selectedService));
      setHasSavedFloorPlan(Boolean(savedSnapshot) || nextPresets.length > 0);
      return true;
    } catch (err) {
      console.error('Erreur lors de la suppression d\'un plan nomme:', err);
      return false;
    }
  };

  const handleTableUpdate = async (tableId, guestCount, reservationName, reservationTime) => {
    try {
      setTables(prevTables => prevTables.map((table) => {
        if (table.id !== tableId) {
          return table;
        }

        const guests = Math.max(0, Math.min(guestCount, table.capacity));
        return {
          ...table,
          guests,
          status: guests > 0 ? 'occupied' : 'free',
          reservationName: reservationName || '',
          reservationTime: reservationTime || ''
        };
      }));
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la table:', err);
    }
  };

  const handleDevTableUpdate = async (tableId, updates) => {
    try {
      const currentTable = tables.find((table) => table.id === tableId);
      if (!currentTable) {
        return null;
      }

      const updatedTable = normalizeTables([{ ...currentTable, ...updates }])[0];
      setTables(prevTables => prevTables.map(t => t.id === tableId ? updatedTable : t));
      return updatedTable;
    } catch (err) {
      console.error('Erreur lors de la mise à jour dev:', err);
      return null;
    }
  };

  const handleDevTableCreate = async () => {
    try {
      const maxId = tables.reduce((max, t) => Math.max(max, t.id), 0);
      const nextNumber = tables.reduce((max, t) => Math.max(max, t.number), 0) + 1;
      const newTable = {
        id: maxId + 1,
        number: nextNumber,
        capacity: 2,
        type: 'rect-small',
        x: 50,
        y: 50,
        guests: 0,
        status: 'free',
        reservationName: '',
        reservationTime: '',
        groupId: null
      };
      setTables(prevTables => [...prevTables, newTable]);
      return newTable;
    } catch (err) {
      console.error('Erreur lors de la création de la table dev:', err);
      return null;
    }
  };

  const handleDevTableDelete = async (tableId) => {
    try {
      setTables(prevTables => {
        const deleted = prevTables.find(t => t.id === tableId);
        const nextTables = prevTables.filter(t => t.id !== tableId);
        if (!deleted?.groupId) {
          return nextTables;
        }
        return nextTables.map(t => t.groupId === deleted.groupId ? { ...t, groupId: null } : t);
      });
      return true;
    } catch (err) {
      console.error('Erreur lors de la suppression de la table dev:', err);
      return false;
    }
  };

  const handleEmbedTables = async (sourceId, targetId) => {
    try {
      const source = tables.find((t) => t.id === sourceId);
      const target = tables.find((t) => t.id === targetId);
      if (!source || !target || source.id === target.id) {
        return false;
      }

      const groupId = source.groupId || target.groupId || `group-${Math.min(source.id, target.id)}-${Math.max(source.id, target.id)}`;
      setTables((prevTables) => {
        const source = prevTables.find((t) => t.id === sourceId);
        const target = prevTables.find((t) => t.id === targetId);
        if (!source || !target) {
          return prevTables;
        }

        const sourceGroup = source.groupId;
        const targetGroup = target.groupId;
        return prevTables.map((t) => {
          const inSourceGroup = sourceGroup && t.groupId === sourceGroup;
          const inTargetGroup = targetGroup && t.groupId === targetGroup;
          if (t.id === sourceId || t.id === targetId || inSourceGroup || inTargetGroup) {
            return { ...t, groupId };
          }
          return t;
        });
      });
      return true;
    } catch (err) {
      console.error('Erreur lors de l\'emboitage des tables:', err);
      return false;
    }
  };

  const handleUnembedGroup = async (groupId) => {
    try {
      setTables((prevTables) => prevTables.map((t) => (
        t.groupId === groupId ? { ...t, groupId: null } : t
      )));
      return true;
    } catch (err) {
      console.error('Erreur lors du desemboitage du groupe:', err);
      return false;
    }
  };

  const handleSaveFloorPlanSnapshot = async () => {
    try {
      localStorage.setItem(getFloorPlanSnapshotKey(selectedService), JSON.stringify(tables));
      setHasSavedFloorPlan(true);
      return true;
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du plan:', err);
      return false;
    }
  };

  const handleRestoreFloorPlanSnapshot = async () => {
    try {
      const savedSnapshot = localStorage.getItem(getFloorPlanSnapshotKey(selectedService));
      if (!savedSnapshot) {
        return false;
      }

      const snapshotTables = JSON.parse(savedSnapshot);
      setTables(snapshotTables);
      localStorage.setItem(getFloorPlanStateKey(selectedService), JSON.stringify(snapshotTables));

      return true;
    } catch (err) {
      console.error('Erreur lors de la restauration du plan:', err);
      return false;
    }
  };

  const handleResetCurrentService = async () => {
    try {
      let serviceOrigin = localStorage.getItem(getFloorPlanOriginKey(selectedService));
      if (!serviceOrigin) {
        const template = localStorage.getItem(FLOOR_PLAN_TEMPLATE_KEY);
        serviceOrigin = template || JSON.stringify(tables);
        localStorage.setItem(getFloorPlanOriginKey(selectedService), serviceOrigin);
      }

      const resetTables = JSON.parse(serviceOrigin).map((table) => ({
        ...table,
        guests: 0,
        status: 'free',
        reservationName: '',
        reservationTime: '',
        groupId: null
      }));

      setTables(resetTables);
      localStorage.setItem(getFloorPlanStateKey(selectedService), JSON.stringify(resetTables));

      return true;
    } catch (err) {
      console.error('Erreur lors de la reinitialisation du service:', err);
      return false;
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-bar">
          <div className="app-brand">
            <div className="app-logo" aria-hidden="true">PS</div>
            <div>
              <h1>PLAN DE SALLE</h1>
              <p>Gestion des tables du restaurant</p>
            </div>
          </div>

          <div className="app-service-menu">
            <label htmlFor="service-select">Service</label>
            <select
              id="service-select"
              value={selectedService}
              onChange={(e) => handleServiceChange(e.target.value)}
            >
              {SERVICE_OPTIONS.map((service) => (
                <option key={service.id} value={service.id}>{service.label}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <p>Assurez-vous que le serveur API est bien démarré</p>
        </div>
      )}

      {!loading && tables.length > 0 && (
        <main className="app-main">
          <FloorPlan 
            tables={tables}
            onTableUpdate={handleTableUpdate}
            onDevMode={() => setIsDevOpen(true)}
            onEmbedTables={handleEmbedTables}
            onUnembedGroup={handleUnembedGroup}
            onResetCurrentService={handleResetCurrentService}
          />
          <DevMode
            isOpen={isDevOpen}
            onClose={() => setIsDevOpen(false)}
            tables={tables}
            onTableUpdate={handleDevTableUpdate}
            onTableCreate={handleDevTableCreate}
            onTableDelete={handleDevTableDelete}
            onSaveFloorPlanSnapshot={handleSaveFloorPlanSnapshot}
            onRestoreFloorPlanSnapshot={handleRestoreFloorPlanSnapshot}
            onSaveNamedFloorPlan={handleSaveNamedFloorPlan}
            onLoadNamedFloorPlan={handleLoadNamedFloorPlan}
            onDeleteNamedFloorPlan={handleDeleteNamedFloorPlan}
            hasSavedFloorPlan={hasSavedFloorPlan}
            savedFloorPlans={savedFloorPlans}
            selectedServiceLabel={SERVICE_OPTIONS.find((service) => service.id === selectedService)?.label || 'Service'}
          />
        </main>
      )}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement du plan de salle...</p>
        </div>
      )}
    </div>
  );
}

export default App;
